import { Box, Button, Flex, Heading, Text, Tooltip } from '@chakra-ui/react';
import { Dispatch, PropsWithChildren, SetStateAction } from 'react';

import PackageIcon from '@/components/PackageIcon';
import StarBadge from '@/components/StarBadge';
import { useCurrentUser } from '@/contexts/currentUser';
import { RegistryPackage } from '@/graphql/upbound-graphql';
import UpboundOfficialIcon from '@/icons/UpboundOfficialIcon';

import PackageLegal from './PackageLegal';
import PackageListBreadcrumbs from './PackageListBreadcrumbs';
import PackageListLink from './PackageListLink';
import PackageMaintainer from './PackageMaintainer';
import PackageOverview from './PackageOverview';
import PackageSource from './PackageSource';
import PackageType from './PackageType';
import PackageVersionMenu from './PackageVersionMenu';

const FamilyProviderHeader: React.FC<
  PropsWithChildren<{
    data: RegistryPackage;
    setIsInstallModalVisible: Dispatch<SetStateAction<boolean>>;
    onStarClick: () => void;
    isStarred: boolean;
  }>
> = ({ data, setIsInstallModalVisible, onStarClick, isStarred }) => {
  const { currentUser } = useCurrentUser();

  const tooltip = 'You must be signed in to star a repository';

  return (
    <Flex flexDirection={{ base: 'column', lg: 'row' }} width="100%">
      <PackageListLink display={{ base: 'inline', lg: 'none' }} mb="25px" data={data} />
      <Flex flexDirection="column" alignItems="center" mr={{ base: '0', lg: '125px' }} minWidth="265px">
        <Flex
          p="0 20px"
          mb="30px"
          height="253px"
          width="100%"
          border="1px solid"
          borderRadius="20px"
          borderColor="white.5"
          backgroundColor="white.0"
          position="relative"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          boxShadow="0 3px 10px 0 rgba(0, 0, 0, 0.03)"
        >
          <PackageIcon data={data} width="64px" height="64px" m="auto auto 20px auto" />
          <Box mb="auto">
            <Heading as="h3" size="md" fontSize="20px" lineHeight="25px" noOfLines={2} width="100%">
              {data.displayName} (Family)
            </Heading>
            {data.official ? (
              <Flex mt="30px" alignItems="center" justifyContent="center">
                <UpboundOfficialIcon role="UpboundOfficialIcon" color="purple.6" width="110px" />
              </Flex>
            ) : (
              <Text size="sm" noOfLines={1} mt="30px">
                {data.account}/{data.name}
              </Text>
            )}
          </Box>
        </Flex>
        <Button
          variant="blackOutline"
          size="md"
          width="100%"
          fontWeight={900}
          onClick={() => setIsInstallModalVisible(true)}
        >
          Install Manifest
        </Button>
        <Flex justifyContent="center" display={{ base: 'block', xl: 'none' }} alignItems="center">
          <Tooltip label={tooltip} placement="bottom" isDisabled={!!currentUser}>
            <Flex
              onClick={e => {
                e.preventDefault();
                onStarClick();
              }}
              mt={{ base: '20px', xl: '0' }}
              px="15px"
              py="5px"
              cursor="pointer"
              alignItems="center"
              justifyContent="center"
              backgroundColor="white.0"
              _hover={{
                border: '1px solid',
                borderColor: 'black.0',
              }}
              border="1px solid"
              borderColor="white.5"
              borderRadius="5px"
              minWidth="110px"
            >
              <Box mr="6px">
                <StarBadge isTooltipDisabled={true} isSignedIn={!!currentUser} isStarred={isStarred} />
              </Box>
              <Text as="span" color="grey.0" fontSize="14px">
                {isStarred ? 'Starred' : 'Star'}
              </Text>
            </Flex>
          </Tooltip>
          {data && data.versions && data.versions.length >= 2 && (
            <Box
              display={{ base: 'block', xl: 'none' }}
              position="relative"
              width={{ base: '100%', xl: '130px' }}
              mt={{ base: '20px', xl: '0' }}
              mb={{ base: '50px', xl: '0' }}
            >
              <PackageVersionMenu data={data} />
            </Box>
          )}
        </Flex>
      </Flex>
      <Flex flexDirection="column" width="100%" flexShrink={1} flexGrow={0} overflow="auto" minHeight="326.5px">
        <Flex justifyContent="space-between">
          <Flex display={{ base: 'none', lg: 'flex' }} alignItems="center">
            <PackageListLink data={data} />
            <PackageListBreadcrumbs data={data} />
          </Flex>
          <Flex display={{ base: 'none', xl: 'flex' }} alignItems="center">
            <Tooltip label={tooltip} placement="bottom" isDisabled={!!currentUser}>
              <Flex
                onClick={e => {
                  e.preventDefault();
                  onStarClick();
                }}
                mr="10px"
                px="15px"
                py="5px"
                alignItems="center"
                justifyContent="center"
                backgroundColor="white.0"
                _hover={{
                  border: '1px solid',
                  borderColor: 'black.0',
                }}
                cursor="pointer"
                border="1px solid"
                borderColor="white.5"
                borderRadius="5px"
                minWidth="110px"
              >
                <Box mr="6px">
                  <StarBadge isTooltipDisabled={true} isSignedIn={!!currentUser} isStarred={isStarred} />
                </Box>
                <Text as="span" color="grey.0" fontSize="14px">
                  {isStarred ? 'Starred' : 'Star'}
                </Text>
              </Flex>
            </Tooltip>
            {data && data.versions && data.versions.length >= 2 && (
              <Box
                width={{ base: '100%', xl: '130px' }}
                position="relative"
                ml={{ base: '0', md: 'auto' }}
                maxWidth="100%"
              >
                <PackageVersionMenu data={data} />
              </Box>
            )}
          </Flex>
        </Flex>
        <PackageOverview data={data} mb={{ base: '50px', md: '39px' }} mt="39px" />
        <Flex flexDirection={{ base: 'column', xl: 'row' }} justifyContent="flex-start">
          <PackageType data={data} />
          <PackageMaintainer data={data} />
          <PackageSource data={data} />
          <PackageLegal data={data} />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FamilyProviderHeader;
